import { create } from "zustand";
import { persist } from "zustand/middleware";
import { get_user_onboarding, complete_onboarding_step, reset_onboarding_step } from "@/api/user";

interface OnboardingState {
  completedSteps: string[];
  isLoading: boolean;
  fetchOnboardingStatus: () => Promise<void>;
  markStepComplete: (stepId: string) => Promise<void>;
  resetStep: (stepId: string) => Promise<void>;
  shouldShowTour: (stepId: string) => boolean;
  syncFromUser: (steps: string[]) => void;
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set, get) => ({
      completedSteps: [],
      isLoading: false,

      syncFromUser: (steps: string[]) => {
        if (!Array.isArray(steps)) return;
        const current = get().completedSteps;
        const merged = Array.from(new Set([...current, ...steps]));
        if (merged.length !== current.length) {
          set({ completedSteps: merged });
        }
      },

      fetchOnboardingStatus: async () => {
        set({ isLoading: true });
        try {
          const completed = await get_user_onboarding();
          if (Array.isArray(completed)) {
            const current = get().completedSteps;
            const merged = Array.from(new Set([...current, ...completed]));
            set({ completedSteps: merged });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to fetch onboarding status:", error);
        } finally {
          set({ isLoading: false });
        }
      },

      markStepComplete: async (stepId: string) => {
        const { completedSteps } = get();
        if (completedSteps.includes(stepId)) return;

        // Optimistic update
        set({ completedSteps: [...completedSteps, stepId] });

        try {
          await complete_onboarding_step(stepId);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to mark step complete:", error);
        }
      },

      resetStep: async (stepId: string) => {
        const { completedSteps } = get();
        set({ completedSteps: completedSteps.filter((s) => s !== stepId) });

        try {
          await reset_onboarding_step(stepId);
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to reset step:", error);
        }
      },

      shouldShowTour: (stepId: string) => {
        const { completedSteps } = get();
        return !completedSteps.includes(stepId);
      },
    }),
    {
      name: "onboarding-storage",
      partialize: (state) => ({ completedSteps: state.completedSteps }),
    }
  )
);
